import { useState, useRef, useEffect, useMemo } from "react";
import styles from "../../components/templates/Tab/Tab.module.scss";
import { generateColor, getInitials } from "./enums";
import Button from "@components/base/button";
import { Search, X } from "react-feather";

export const ClassifierTabComponent = ({ options, onClick, activeTab }) => {
  return (
    <div className={styles.tabContainer}>
      {options.map((tab, index) => (
        <span
          className={`${activeTab === index ? styles.activeTab : ""} ${styles.tabStyling}`}
          onClick={() => {
            if (activeTab === index) return;
            onClick(index);
          }}
          key={tab.value}>
          {tab.name}
        </span>
      ))}
    </div>
  );
};

export const classificationOptions = [
  { value: "import", label: "Import Classification" },
  { value: "export", label: "Export Classification" },
];

export const selectStyle = {
  control: (base, state) => ({
    ...base,
    "&:active": {
      border: "solid 1px #7143f1",
    },
    "&:hover": {
      boxShadow: "none",
    },
    minHeight: 48,
    width: "100%",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#fff",
    border: state.isFocused ? "1px solid #7143f1" : "1px solid #d0d5dd",
    cursor: state.isDisabled ? "not-allowed !important" : "pointer !important",
    pointerEvents: "auto !important",
    backgroundColor: "#fff",
  }),
  menu: (base) => ({
    ...base,
    fontSize: "14px",
    fontWeight: "normal",
    padding: "4px",
    color: "#000",
    border: "solid 1px #7143f1",
    boxShadow: "none",
    borderRadius: "4px",
    backgroundColor: "#fff",
    width: "100%",
  }),
  option: (base, { isSelected }) => ({
    ...base,
    "&:hover": {
      boxShadow: "none",
      backgroundColor: "#eaecf0",
    },
    backgroundColor: isSelected ? "rgba(108, 56, 255, 0.2) !important" : "none",
    color: "#101828",
    cursor: "pointer !important",
    marginBottom: "2px !important",
    width: "100%",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

export const CustomSelect = ({ options, value, onChange, placeholder = "Select...", disabled = false, className = "", valueSearch = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectRef = useRef(null);
  const searchRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    const filtered = options.filter((option) => {
      const isLabelMatch = typeof option.label === "string" ? option.label?.toLowerCase().includes(searchTermLower) : option.labelValue?.toLowerCase().includes(searchTermLower);
      const isValueMatch = option.value.toLowerCase().includes(searchTermLower);
      return valueSearch ? isLabelMatch || isValueMatch : isLabelMatch;
    });

    if (searchTermLower.length === 2) {
      return filtered.sort((a) => (a.value.toLowerCase() === searchTermLower ? -1 : 1));
    }

    return filtered;
  }, [options, searchTerm, valueSearch]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const renderOptions = () => {
    if (filteredOptions.length === 0) {
      return <div className="tw-px-4 tw-py-3 tw-text-gray-500 tw-text-center">No options found</div>;
    }
    return filteredOptions.map((option) => (
      <button key={option.value} type="button" onClick={() => handleSelect(option)} className={`tw-w-full tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-200 tw-transition-colors tw-bg-white tw-duration-150 ${option.value === value?.value ? "tw-bg-blue-100 tw-text-blue-600 tw-font-medium" : ""}`}>
        <span>{option.label}</span>
      </button>
    ));
  };

  return (
    <div className={`tw-relative tw-w-full ${className}`} ref={selectRef}>
      <div type="button" onClick={() => !disabled && setIsOpen(!isOpen)} className={`tw-w-full tw-px-4 tw-py-3 tw-text-left tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-flex tw-items-center tw-justify-between tw-transition-all tw-duration-200 ${disabled ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed" : "hover:tw-border-gray-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-purple-500 focus:tw-border-primary-purple-500"}`}>
        <span className={value ? "tw-text-gray-500 tw-text-sm" : "tw-text-gray-500 tw-text-sm"}>{value ? value.label : placeholder}</span>
        <svg className={`tw-w-5 tw-h-5 tw-text-gray-400 tw-transition-transform tw-duration-200 ${isOpen ? "tw-transform tw-rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg">
          <div className="tw-p-3 tw-border-b tw-border-gray-200">
            <div className="tw-relative">
              <input ref={searchRef} type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="tw-w-full tw-px-3 tw-py-2 tw-pl-10 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-border-blue-500" />
              <svg className="tw-absolute tw-left-3 tw-top-3 tw-transform tw-translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <div className="tw-max-h-60 tw-overflow-y-auto">{renderOptions()}</div>
        </div>
      )}
    </div>
  );
};

export const BoxLoader = ({ messages, changeInterval = 10000, boxCount = 10 }) => {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (index === messages.length - 1) return;

    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev < messages.length - 1 ? prev + 1 : prev));
        setFade(true);
      }, 300);
    }, changeInterval);

    return () => clearInterval(interval);
  }, [index, messages.length, changeInterval]);

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[200px] tw-px-4">
      <div className="tw-flex tw-gap-1 tw-mb-4 tw-flex-wrap tw-justify-center tw-max-w-full">
        {Array.from({ length: boxCount }).map((_, i) => (
          <div
            key={i}
            className="tw-w-6 tw-h-6 sm:tw-w-8 sm:tw-h-8 md:tw-w-9 md:tw-h-9 tw-bg-gray-300 tw-rounded-sm tw-animate-pulse"
            style={{
              animationDelay: `${i * 0.15}s`,
              animationDuration: "1.2s",
              animationIterationCount: "infinite",
            }}
          />
        ))}
      </div>
      <p className={`tw-text-sm tw-font-medium tw-text-center tw-px-2 tw-transition-opacity tw-duration-300 ${fade ? "tw-opacity-100" : "tw-opacity-0"}`}>{messages[index]}</p>
    </div>
  );
};

export const boxLoaderMessages = ["Analysing Chapter", "Mapping Chapter as per WCO notes and rulings", "Analysing Heading and Sub Headings", "Mapping Heading and Sub Headings as per WCO notes and rulings", "Mapping Dutiable HS Codes", "Creating Justification"];

export const UserAvatar = ({ name, theme = "light", showName = true, className = "" }) => {
  const safeName = name || "";
  const initials = useMemo(() => getInitials(safeName), [safeName]);
  const backgroundColor = useMemo(() => generateColor(safeName, theme === "dark"), [safeName, theme]);

  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <div className={`tw-flex tw-items-center tw-justify-center tw-size-8 tw-text-sm tw-rounded-full tw-font-medium tw-text-gray-700 ${className}`} style={{ backgroundColor }}>
        {initials}
      </div>
      {showName && <span className="tw-capitalize">{safeName}</span>}
    </div>
  );
};

export const TriggerSearch = ({ onSubmit, onChange, onClear, value, placeholder, disabled = false }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!disabled && value) {
      onSubmit(value);
    }
  };

  const handleClear = () => {
    if (!disabled) {
      onClear();
    }
  };

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <form onSubmit={handleSubmit} className="tw-flex tw-items-center tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-25 tw-h-8 tw-overflow-hidden tw-relative">
        <div className="tw-flex tw-items-center tw-w-full">
          <Search size={14} color="#101828" className="tw-ml-2" />
          <input className="tw-flex-1 tw-h-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-gray-400 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 tw-pl-1 tw-pr-8" placeholder={placeholder || "Search..."} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
          {value && !disabled && <X size={14} className="tw-absolute tw-right-2 tw-cursor-pointer" onClick={handleClear} />}
        </div>
      </form>
      <Button type="submit" variant="primaryWithIcon" aria-label="Search" disabled={disabled || !value} className="tw-px-3 tw-h-full disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" onClick={handleSubmit}>
        <Search size={16} color="#ffffff" />
      </Button>
    </div>
  );
};

export default { BoxLoader, CustomSelect, ClassifierTabComponent, classificationOptions, selectStyle, boxLoaderMessages, UserAvatar, TriggerSearch };
